/**
 * Shared demo framework — registry.
 *
 * Each demo registers itself once (side-effecting import from `@/demo/init`)
 * with its key, label, accent colors, a one-click reset, and the store provider
 * that re-seeds it on mount. The shared `<DemoStoreProvider>` and
 * `<DemoControls>` resolve the active demo from here, so the route wiring stays
 * demo-agnostic — adding a new demo never touches the route.
 */

import type { PropsWithChildren, ReactNode } from 'react';

export interface RegisteredDemo {
  /** Short key matching the `?demo=<key>` value (e.g. 'ar', 'ap'). */
  key: string;
  /** Badge label shown in the floating controls (e.g. 'AP Demo Mode'). */
  label: string;
  /** Tailwind classes for the badge accent. */
  accent?: {
    badge?: string;
    label?: string;
    resetButton?: string;
  };
  /** Re-seed this demo's store(s) to their initial snapshot. */
  reset: () => void;
  /** Provider that re-seeds the store on mount (a clean deletion boundary). */
  StoreProvider: (props: PropsWithChildren) => ReactNode;
}

const registry = new Map<string, RegisteredDemo>();

/** Register (replace) a demo by key. Called at module load via `@/demo/init`. */
export function registerDemo(demo: RegisteredDemo): void {
  registry.set(demo.key, demo);
}

/** Look up a registered demo by key. */
export function getRegisteredDemo(key: string): RegisteredDemo | undefined {
  return registry.get(key);
}
